import { useState } from 'react';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { UnwindTrace } from '../../../types/unwind-debug.types';
import { CATEGORY_COLOR, LEVEL_COLOR } from './levelColors';

export interface TraceRowProps {
  trace: UnwindTrace;
}

export function TraceRow({ trace }: TraceRowProps) {
  const [open, setOpen] = useState(false);
  const ts = new Date(trace.createdAt);
  const tsLabel = `${ts.toLocaleTimeString()}.${String(ts.getMilliseconds()).padStart(3, '0')}`;

  const catColor = CATEGORY_COLOR[trace.category] ?? '#666';
  const lvlColor = LEVEL_COLOR[trace.level] ?? '#666';

  const hasPayload = trace.payload && Object.keys(trace.payload).length > 0;

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${catColor}`,
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        py: 0.5,
        pl: 1,
        pr: 1,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: '0.72rem',
        cursor: hasPayload ? 'pointer' : 'default',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
      }}
      onClick={() => hasPayload && setOpen((o) => !o)}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          size="small"
          sx={{ p: 0, visibility: hasPayload ? 'visible' : 'hidden' }}
        >
          {open ? <KeyboardArrowDownIcon fontSize="inherit" /> : <KeyboardArrowRightIcon fontSize="inherit" />}
        </IconButton>
        <Typography sx={{ width: 110, color: 'grey.500', fontSize: 'inherit' }}>{tsLabel}</Typography>
        <Chip
          label={trace.category}
          size="small"
          sx={{
            height: 16,
            fontSize: '0.62rem',
            bgcolor: catColor,
            color: 'white',
            '.MuiChip-label': { px: 0.6 },
          }}
        />
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: lvlColor }} />
        {trace.pairName && (
          <Typography sx={{ width: 180, color: 'grey.300', fontSize: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {trace.pairName}
          </Typography>
        )}
        {trace.platform && (
          <Typography sx={{ width: 70, color: 'grey.500', fontSize: 'inherit' }}>
            {trace.platform}
          </Typography>
        )}
        <Typography sx={{ width: 160, color: 'grey.400', fontSize: 'inherit' }}>{trace.event}</Typography>
        <Typography sx={{ flex: 1, color: 'grey.100', fontSize: 'inherit' }}>{trace.message}</Typography>
      </Stack>
      {open && hasPayload && (
        <Box
          sx={{
            mt: 0.5,
            ml: 4,
            p: 1,
            bgcolor: 'rgba(0,0,0,0.3)',
            borderRadius: 1,
            color: 'grey.300',
            whiteSpace: 'pre',
            overflowX: 'auto',
            fontSize: '0.7rem',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {JSON.stringify(trace.payload, null, 2)}
        </Box>
      )}
    </Box>
  );
}
